import type { NewEvent } from '@ilteducation/activity-stream-types';
import { captureSentryException } from '@ilteducation/sentry-vue3';
import { sendEventToActivityStream } from './api';
import { countEvents, enqueueEvents, popBatch } from './event-queue';

const BATCH_SIZE = 25; // a somewhat random limit...

const FLUSH_DEBOUNCE_MS = 1000;

let flushTimeout: NodeJS.Timeout | undefined;
let flushPromise: Promise<void> | null = null;

/**
 * Retry send a batch of events
 *
 * @param events
 * @param attemptNumber
 */
const sendBatch = async (events: NewEvent[], attemptNumber: number = 1): Promise<void> => {
  const maxRetries = 10;

  if (attemptNumber > maxRetries) {
    // We already capture the exception thrown when trying to send events, no need to throw error here.
    console.error(
      `Failed to send batch of events after ${maxRetries} attempts. Dropping batch. Events: ${JSON.stringify(events)}`,
    );
    return;
  }

  try {
    await sendEventToActivityStream(events);
  } catch (error) {
    captureSentryException(error);

    const backoffTime = Math.pow(2, attemptNumber) * 1000 + Math.random() * 2000;
    await new Promise((resolve) => {
      setTimeout(resolve, backoffTime);
    });

    await sendBatch(events, attemptNumber + 1);
  }
};

const flush = async (): Promise<void> => {
  if (flushTimeout) {
    clearTimeout(flushTimeout);
    flushTimeout = undefined;
  }

  // Only one flush at a time
  if (flushPromise) {
    return flushPromise;
  }

  flushPromise = (async () => {
    try {
      const eventsBatch = await popBatch(BATCH_SIZE);

      if (eventsBatch.length === 0) {
        return;
      }

      await sendBatch(eventsBatch);

      // At the end of a flush, we check if we should flush again, since more events could be left in the buffer
      if (await hasBufferedEvents()) {
        // We want a bit of delay between requests
        scheduleFlush();
      }
    } catch (err) {
      captureSentryException(err);
    } finally {
      flushPromise = null;
    }
  })();

  return flushPromise;
};
const scheduleFlush = () => {
  if (!flushTimeout) {
    flushTimeout = setTimeout(flush, FLUSH_DEBOUNCE_MS);
  }
};

const getIsBufferFull = async (): Promise<boolean> => (await countEvents()) >= BATCH_SIZE;

const hasBufferedEvents = async (): Promise<boolean> => (await countEvents()) > 0;

/*
 A page being left is gone before an IndexedDB round-trip resolves, so what is flushed during
 pagehide goes straight out on the keepalive request. visibilityState is no use here: Chrome still
 reports 'visible' while pagehide runs. Registered at import, so it runs before the player's own
 pagehide listeners.
 */
let isPageHiding = false;
window.addEventListener('pagehide', () => (isPageHiding = true), { capture: true });
window.addEventListener('pageshow', () => (isPageHiding = false));

const addEvents = async (events: NewEvent[], forceFlush?: boolean): Promise<void> => {
  if (forceFlush && isPageHiding) {
    sendEventToActivityStream(events).catch(() => enqueueEvents(events));
    return;
  }

  try {
    /**
     * Store events in persistent buffer
     */
    await enqueueEvents(events);

    const isFlushingScheduled = !!flushTimeout;

    const isBufferFull = await getIsBufferFull();

    if (isBufferFull || forceFlush) {
      flush();
    } else if (!isFlushingScheduled) {
      scheduleFlush();
    }
  } catch (err) {
    captureSentryException(err);
  }
};

export const sendEvents = (events: NewEvent[], flush?: boolean): void => {
  addEvents(events, flush);
};

export const sendEvent = (event: NewEvent, flush?: boolean): void => sendEvents([event], flush);
